/**
 * The privacy policy, in the app and in the reader's language (`/privacy`).
 *
 * `ConsentGate` records consent to `PRIVACY_POLICY_VERSION`, which
 * `backend/src/pivot/security/consent.py` says is "published in PRIVACY.md".
 * That file sits at the repo root: not in `public/`, not served by the SPA
 * mount, not linked anywhere. Consenting to a document you cannot open is not
 * consent (constitution §V), so the policy lives here, on a route.
 *
 * **Why the text is in the dictionaries and not a copy of the .md file.**
 * Serving `PRIVACY.md` from `public/` would have been one line, but it would
 * have shipped an unstyled English-and-two-appendices blob, and the consent
 * copy is what the candidate is agreeing to — it has to be in their language.
 * PRIVACY.md already carries FR and NL sections; those are the source of the
 * text below.
 *
 * **Trap: two copies of one legal document.** PRIVACY.md stays the published,
 * versioned original — `PRIVACY_POLICY_VERSION` names *it*, and stored consents
 * point at that string. Change one and change the other in the same commit, and
 * bump `VERSION` here and there together if the change requires renewed
 * consent. PIVOT-288 keeps version 2026-07-26 for this development update
 * because every current account is a test user or a friend and the dataset
 * will be deleted. The launch gate requires a new version before a non-friend
 * joins.
 */

import { Link } from 'react-router'
import { LangSwitch } from '../components/LangSwitch'
import { ThemeToggle } from '../components/ThemeToggle'
import { useI18n } from '../i18n'
import '../styles/header-actions.css'
import './Privacy.css'

/** Kept in step with `security/consent.py::PRIVACY_POLICY_VERSION`. */
const VERSION = '2026-07-26'
const UPDATED = '2026-08-26'

export default function Privacy() {
  const { t } = useI18n()

  return (
    <div className="privacy-page">
      <title>{`${t('privacy.title')} — ${t('common.appName')}`}</title>

      <header className="privacy-head">
        <Link to="/" className="privacy-back">
          {t('privacy.back')}
        </Link>
        <div className="header-actions">
          <LangSwitch />
          <ThemeToggle />
        </div>
      </header>

      <h1>{t('privacy.title')}</h1>
      <p className="eyebrow">{t('privacy.version', { version: VERSION, updated: UPDATED })}</p>
      <p className="privacy-intro">{t('privacy.intro')}</p>

      <section className="privacy-section">
        <h2>{t('privacy.whatTitle')}</h2>
        <p>{t('privacy.whatBody')}</p>
      </section>

      <section className="privacy-section">
        <h2>{t('privacy.collectTitle')}</h2>
        <ul className="privacy-list">
          <li>{t('privacy.collectCv')}</li>
          <li>{t('privacy.collectProfile')}</li>
          <li>{t('privacy.collectAnswers')}</li>
          <li>{t('privacy.collectAccount')}</li>
          <li>{t('privacy.collectInterest')}</li>
          <li>{t('privacy.collectConsent')}</li>
        </ul>
        <p>{t('privacy.collectNever')}</p>
      </section>

      <section className="privacy-section">
        <h2>{t('privacy.anthropicTitle')}</h2>
        <p>{t('privacy.anthropicBody')}</p>
      </section>

      <section className="privacy-section">
        <h2>{t('privacy.residencyTitle')}</h2>
        <p>{t('privacy.residencyBody')}</p>
      </section>

      <section className="privacy-section">
        <h2>{t('privacy.storageTitle')}</h2>
        <p>{t('privacy.storageBody')}</p>
        <ul className="privacy-list">
          <li>{t('privacy.storageSession')}</li>
          <li>{t('privacy.storageLocale')}</li>
        </ul>
        <p>{t('privacy.storageGoogle')}</p>
        <p>{t('privacy.storageControl')}</p>
      </section>

      <section className="privacy-section">
        <h2>{t('privacy.consentTitle')}</h2>
        <p>{t('privacy.consentBody')}</p>
      </section>

      <section className="privacy-section">
        <h2>{t('privacy.erasureTitle')}</h2>
        <p>{t('privacy.erasureBody')}</p>
      </section>

      <section className="privacy-section">
        <h2>{t('privacy.minimizationTitle')}</h2>
        <p>{t('privacy.minimizationBody')}</p>
      </section>

      <section className="privacy-section">
        <h2>{t('privacy.contactTitle')}</h2>
        <p>{t('privacy.contactBody')}</p>
      </section>

      {/* Stated, not buried: no legal review yet (constitution §VII). */}
      <section className="privacy-section privacy-status">
        <h2>{t('privacy.statusTitle')}</h2>
        <p>{t('privacy.statusBody')}</p>
      </section>
    </div>
  )
}
